import { useState } from 'react';
import { FormSubmission } from '../types';
import styles from '../form-submissions.module.css';
import { Mail, Phone, MapPin, Linkedin, FileText, FileDown } from 'lucide-react';
import { LinkedInModal, LinkedInProfileData } from './LinkedInModal';
import { ResumeModal, ResumeData } from './ResumeModal';
import { adityaLinkedInProfile, adityaResumeData } from '../dummyData';

interface ApplicantHeroProps {
  submission: FormSubmission;
}

// Default LinkedIn profile data for other submissions
const getDefaultLinkedInProfile = (submission: FormSubmission): LinkedInProfileData => ({
  name: submission.contact.fullName,
  headline: `${submission.professional.profession} with ${submission.professional.yearsOfExperience} years experience`,
  location: submission.contact.countryOfCitizenship,
  email: submission.contact.email,
  currentCompany: submission.evidence.criticalRoles[0]?.organization || 'N/A',
  currentRole: submission.evidence.criticalRoles[0]?.role || submission.professional.profession,
  experience: submission.evidence.criticalRoles.map(role => ({
    company: role.organization || '',
    role: role.role || '',
    duration: role.date || '',
    description: role.description,
  })),
  education: [
    {
      institution: 'University',
      degree: submission.professional.highestEducation,
      field: submission.professional.fieldOfStudy,
      years: 'N/A',
    },
  ],
  skills: ['Leadership', 'Problem Solving', 'Communication', 'Technical Expertise'],
});

// Default Resume data for other submissions
const getDefaultResumeData = (submission: FormSubmission): ResumeData => ({
  name: submission.contact.fullName,
  email: submission.contact.email,
  phone: submission.contact.phoneNumber,
  location: submission.contact.countryOfCitizenship,
  summary: `Experienced ${submission.professional.profession} with ${submission.professional.yearsOfExperience} years of expertise in ${submission.professional.fieldOfStudy}.`,
  experience: submission.evidence.criticalRoles.map(role => ({
    role: role.role || '',
    company: role.organization || '',
    duration: role.date || '',
    highlights: [role.description],
  })),
  education: [
    {
      degree: submission.professional.highestEducation,
      institution: 'University',
      years: 'N/A',
      field: submission.professional.fieldOfStudy,
    },
  ],
  skills: ['Leadership', 'Problem Solving', 'Communication', 'Technical Expertise'],
});

export function ApplicantHero({ submission }: ApplicantHeroProps) {
  const { contact, professional, visaType, submittedAt } = submission;

  const [linkedInModalOpen, setLinkedInModalOpen] = useState(false);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);

  // Always use Aditya's dummy data for LinkedIn and Resume
  const linkedInProfile = adityaLinkedInProfile;
  const resumeData = adityaResumeData;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleLinkedInClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setLinkedInModalOpen(true);
  };

  const handleResumeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setResumeModalOpen(true);
  };

  const handleGenerateForm = () => {
    // Create a link element to trigger download
    const link = document.createElement('a');
    link.href = '/Form-I129-Aditya-O1.pdf';
    link.download = 'Form-I129-Aditya-O1.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <div className={styles.avatarWrapper}>
            <div className={styles.avatar}>{getInitials(contact.fullName)}</div>
          </div>

          <div className={styles.heroInfo}>
            <h1 className={styles.heroName}>
              {contact.fullName}
              <span className={`${styles.badge} ${styles.badgeVisa}`}>{visaType}</span>
            </h1>

            <p className={styles.heroProfession}>
              {professional.profession} · {professional.yearsOfExperience} years experience
            </p>

            <div className={styles.contactBadges}>
              <span className={styles.contactBadge}>
                <Mail size={14} />
                <a href={`mailto:${contact.email}`}>{contact.email}</a>
              </span>

              <span className={styles.contactBadge}>
                <Phone size={14} />
                {contact.phoneNumber}
              </span>

              <span className={styles.contactBadge}>
                <MapPin size={14} />
                {contact.countryOfCitizenship}
              </span>

              <span
                className={`${styles.contactBadge} ${styles.contactBadgeClickable}`}
                onClick={handleLinkedInClick}
                title="Click to view LinkedIn profile details"
              >
                <Linkedin size={14} />
                <span>LinkedIn</span>
              </span>

              <span
                className={`${styles.contactBadge} ${styles.contactBadgeClickable}`}
                onClick={handleResumeClick}
                title="Click to view resume"
              >
                <FileText size={14} />
                <span>resume.pdf</span>
              </span>
            </div>

            <p className={styles.textMuted} style={{ fontSize: '12px', marginTop: '12px' }}>
              Submitted {formatDate(submittedAt)}
            </p>
          </div>

          {/* Generate Form Button */}
          <div className={styles.heroActions}>
            <button
              className={styles.generateFormButton}
              onClick={handleGenerateForm}
              title="Download I-129 Form PDF"
            >
              <FileDown size={18} />
              Generate Form I-129
            </button>
          </div>
        </div>
      </section>

      {/* LinkedIn Modal */}
      <LinkedInModal
        isOpen={linkedInModalOpen}
        onClose={() => setLinkedInModalOpen(false)}
        profileData={linkedInProfile}
      />

      {/* Resume Modal */}
      <ResumeModal
        isOpen={resumeModalOpen}
        onClose={() => setResumeModalOpen(false)}
        resumeData={resumeData}
      />
    </>
  );
}
